import { Pill } from '@baseV2'
import { enquiryInputType, strings } from '@constants'
import { getInputType } from '@base/composite/Table/TableUtils/Columns'
import { decimalSeparatedNumber, displayName, formatDate } from '@utils'

// weights come in kgs, and are shown in MT for weight based items
const displayWeight = (weight, inputType) =>
	weight == null ? '-' : decimalSeparatedNumber(inputType === enquiryInputType.WEIGHT ? Number(weight) / 1000 : Number(weight))

// Columns of the dispatch plan cards table, shared by the All and the Active dispatch plan lists.
export const getDispatchPlanCardColumns = ({ hideOrderEnquiryId = false } = {}) => {
	const twoLine = (primary, secondary) => (
		<div>
			<div>{primary ?? '-'}</div>
			<small>
				<b>{secondary ?? '-'}</b>
			</small>
		</div>
	)
	const cityState = (location) => [location?.city, location?.state].filter(Boolean).join(', ') || '-'

	return [
		...(hideOrderEnquiryId
			? []
			: [
					{
						label: strings(['order', 'id']) + ' / ' + strings(['enquiry', 'id']),
						key: (data) => data,
						renderer: (data) => twoLine(data?.order_id, data?.enquiry_id),
						minWidth: 115,
						mobile: true,
					},
			  ]),
		{
			label: `${strings('buyer')} / ${strings(['delivery', 'location'])}`,
			key: (data) => data,
			renderer: (data) => twoLine(data?.buyer_name, cityState(data?.delivery_location)),
			minWidth: 140,
			mobile: true,
		},
		{
			label: `${strings('supplier')} / ${strings(['dispatch', 'location'])}`,
			key: (data) => data,
			renderer: (data) => twoLine(data?.supplier_name, cityState(data?.dispatch_location)),
			minWidth: 140,
			mobile: true,
		},
		{
			label: strings(['logistics', 'partner']),
			key: 'logistics_partner',
			renderer: (data) => displayName(data),
			default: '-',
			minWidth: 100,
			mobile: true,
		},
		{ label: strings('logistics_handler'), key: 'logistics_handler', default: '-', minWidth: 85, mobile: true },
		{
			label: `${strings(['sales', 'person'])} / ${strings(['supply', 'person'])}`,
			key: (data) => data,
			renderer: (data) => twoLine(data?.sales_person_name, data?.supply_person_name),
			minWidth: 110,
			mobile: true,
		},
		{
			label: strings(['dispatch', 'date']),
			key: 'plan_start_date',
			renderer: (data) => <div>{formatDate(data)}</div>,
			minWidth: 95,
			mobile: true,
		},
		{
			label: strings('invoiced_ordered_weight'),
			key: (data) => data,
			renderer: (data) => (
				<div>
					{displayWeight(data?.serviced_weight, data?.input_type)} / {displayWeight(data?.total_allotted_weight, data?.input_type)}{' '}
					{data?.input_type ? getInputType(data.input_type) : ''}
				</div>
			),
			minWidth: 120,
			mobile: true,
		},
		{
			label: strings('vehicle_dispatch_planned_quantity'),
			key: (data) => data,
			renderer: (data) => (
				<div>
					{displayWeight(data?.vehicle_planned_quantity, data?.input_type)} /{' '}
					{displayWeight(data?.dispatch_planned_quantity, data?.input_type)} {data?.input_type ? getInputType(data.input_type) : ''}
				</div>
			),
			minWidth: 120,
			mobile: true,
		},
		{
			label: strings('pending_vehicles'),
			key: (data) => data,
			renderer: (data) => (
				<div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
					<Pill color='warning' variant='subtle' round>
						{data?.vehicle_count - data?.pending_vehicle_count ?? 0}
					</Pill>
					/
					<Pill color='neutral' variant='subtle' round>
						{data?.vehicle_count ?? 0}
					</Pill>
				</div>
			),
			minWidth: 130,
			mobile: true,
		},
		{
			label: strings('delivery', 'status'),
			key: 'delay_days',
			renderer: (data) => (
				<div>
					{data > 0 ? (
						<Pill color='error' variant='subtle' round style={{ whiteSpace: 'normal', textAlign: 'center' }}>
							{strings(['delayed', 'by'])} {data} {strings('days')}
						</Pill>
					) : (
						<Pill color='success' variant='subtle' round>
							{strings('on_time')}
						</Pill>
					)}
				</div>
			),
			minWidth: 110,
			mobile: true,
		},
		// Paginated table: client-side sort would only reorder the current page, so keep all columns non-sortable.
	].map((col) => ({ ...col, sortable: false }))
}
